import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    ReactNode
} from 'react';
import {getWeekInfo} from '../../lib/week-calculator';
import {
    ResourceAllocationState,
    Person,
    Team,
    Role,
    Project,
    ProjectPhase,
    ResourceProjectAssignment,
    Allocation,
    NonWorkingDay,
    PersonalAvailability,
    PersonalAvailabilityWeek,
    ViewMode,
    Tag,
    ProjectPerson,
    RequirementLevel
} from '../../types';
import {WhatIfLens} from '../../calc_engine/transform/scenario-projection';
import {useQueryClient} from '@tanstack/react-query';
import {useCreateAuthenticatedFetch} from '../../api/useCreateAuthenticatedFetch';
import {useSignalR} from '../../contexts/SignalRContext';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import baseUrl from '../../api/baseUrl';
import useSessionStorage from '../../hooks/useSessionStorage';
import {
    usePersonActions,
    useTeamActions,
    useRoleActions,
    useProjectActions,
    useAllocationActions,
    useAvailabilityActions,
    useTagActions,
    useConfigActions
} from './actions';
import {signalRHandlers} from './handlers';

interface ResourceAllocationContextType {
    state: ResourceAllocationState;
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;

    // The what-if lens: null = the published picture (default). Applied
    // centrally in useAllocationLookup — dashboard, utilisation and every
    // insight respect it automatically.
    whatIfLens: WhatIfLens | null;
    setWhatIfLens: (lens: WhatIfLens | null) => void;

    // Bounded-window loading (Phase 2).
    isStateLoading: boolean; // initial load — no usable state yet
    isWindowLoading: boolean; // a window refetch while existing state is still shown
    ensureWindowLoaded: (visibleFrom: number, visibleTo: number) => void;

    // Person operations — "expire" is the default soft retire (keeps history);
    // "permanentlyDelete" is the created-in-error escape hatch (rejects if history exists).
    addPerson: (person: Omit<Person, 'id'>) => Promise<string>;
    updatePerson: (id: string, person: Partial<Person>) => void;
    // Merge server-fetched people into state without overwriting loaded entities
    // (a scoped /state doesn't hold the whole tenant; see Phase 3 doc).
    ensurePeople: (people: Person[]) => void;
    expirePerson: (id: string) => void;
    reinstatePerson: (id: string) => void;
    permanentlyDeletePerson: (id: string) => Promise<void>;
    unlinkPersonUser: (id: string) => void;

    // Team operations
    addTeam: (team: Omit<Team, 'id'>) => Promise<string>;
    updateTeam: (id: string, team: Partial<Team>) => void;
    // Team twin of ensurePeople.
    ensureTeams: (teams: Team[]) => void;
    expireTeam: (id: string) => void;
    reinstateTeam: (id: string) => void;
    permanentlyDeleteTeam: (id: string) => Promise<void>;

    // Role operations
    addRole: (role: Omit<Role, 'id'>) => Promise<string>;
    updateRole: (id: string, role: Partial<Role>) => void;
    expireRole: (id: string) => void;
    reinstateRole: (id: string) => void;
    permanentlyDeleteRole: (id: string) => Promise<void>;

    // Project operations
    addProject: (project: Omit<Project, 'id'>) => Promise<string>;
    updateProject: (id: string, project: Partial<Project>) => void;
    // Atomic project time shift (project window + phases + allocation weeks,
    // one server transaction; converges via the ProjectShifted event). Pass
    // fromWeek to move only the remainder from that week — the spent history
    // stays put (partial moves shift later only).
    shiftProject: (
        id: string,
        offsetWeeks: number,
        fromWeek?: number | null
    ) => Promise<boolean>;
    deleteProject: (id: string) => void;

    // Phase operations
    addPhase: (
        phase: Omit<ProjectPhase, 'id' | 'createdAt' | 'updatedAt'>
    ) => Promise<string>;
    updatePhase: (id: string, phase: Partial<ProjectPhase>) => void;
    deletePhase: (id: string) => void;
    getProjectPhases: (projectId: string) => ProjectPhase[];

    // Assignment operations
    addAssignment: (
        assignment: Omit<ResourceProjectAssignment, 'id' | 'createdAt'>
    ) => Promise<string>;
    updateAssignment: (
        id: string,
        assignment: Partial<ResourceProjectAssignment>
    ) => void;
    deleteAssignment: (id: string) => void;

    // Allocation operations
    addAllocation: (allocation: Omit<Allocation, 'id'>) => Promise<string>;
    updateAllocation: (id: string, allocation: Partial<Allocation>) => void;
    deleteAllocation: (id: string) => void;
    // Multi-cell gesture (type-over selection, paste, move) applied as one
    // optimistic update + one bulk request + one broadcast.
    applyAllocationEdits: (batch: {
        upserts: Array<Omit<Allocation, 'id'> & {id?: string}>;
        deleteIds?: string[];
    }) => Promise<void>;

    // Non-working day operations
    addNonWorkingDay: (nonWorkingDay: Omit<NonWorkingDay, 'id'>) => string;
    updateNonWorkingDay: (
        id: string,
        nonWorkingDay: Partial<NonWorkingDay>
    ) => void;
    deleteNonWorkingDay: (id: string) => void;

    // Personal availability operations
    // Availability twin of ensurePeople — folds records fetched outside /state
    // (a resolved person's page) into the maps, add-only.
    ensurePersonalAvailability: (
        records: PersonalAvailability[],
        weeks: PersonalAvailabilityWeek[]
    ) => void;
    addPersonalAvailability: (
        availability: Omit<PersonalAvailability, 'id'>
    ) => string;
    updatePersonalAvailability: (
        id: string,
        availability: Partial<PersonalAvailability>
    ) => void;
    deletePersonalAvailability: (id: string) => void;

    // Personal availability week operations
    addPersonalAvailabilityWeek: (
        week: Omit<PersonalAvailabilityWeek, 'id'>
    ) => string;
    updatePersonalAvailabilityWeek: (
        id: string,
        updates: Partial<PersonalAvailabilityWeek>
    ) => void;
    deletePersonalAvailabilityWeek: (id: string) => void;
    deletePersonalAvailabilityWeeksInRange: (
        personId: string,
        weekNumbers: number[]
    ) => void;

    // Working hours config operations. options.effectiveFromWeek: undefined =
    // rewrite history; a global week number = dated write (past weeks keep the
    // previously in-force value). See useConfigActions.ConfigWriteOptions.
    updateWorkingHoursConfig: (
        config: Partial<{baseHoursPerWeek: number; defaultHoursPerDay: number}>,
        options?: {effectiveFromWeek?: number}
    ) => void;

    // RAG threshold config operations (additional-demand amber/red + the
    // opt-in baseline amber band; null baselineAmberThreshold = band off)
    updateRagThresholdConfig: (
        config: Partial<{
            additionalDemandAmberThreshold: number;
            additionalDemandRedThreshold: number;
            baselineAmberThreshold: number | null;
        }>,
        options?: {effectiveFromWeek?: number}
    ) => void;

    // Utilisation config operations
    updateUtilisationConfig: (
        config: Partial<{
            defaultTargetPercent: number;
            includeAdditionalDemand: boolean;
        }>,
        options?: {effectiveFromWeek?: number}
    ) => void;

    // Remove one version from an effective-dated config series.
    deleteConfigVersion: (
        configType:
            | 'working-hours'
            | 'rag-threshold'
            | 'utilisation',
        effectiveFromWeek: number
    ) => void;

    // Tag operations
    addTag: (tag: Omit<Tag, 'id' | 'createdAt'>) => Promise<string>;
    updateTag: (id: string, updates: Partial<Tag>) => void;
    expireTag: (id: string) => void;
    reinstateTag: (id: string) => void;
    permanentlyDeleteTag: (id: string) => Promise<void>;

    // Project-Tag association operations
    addProjectTag: (projectId: string, tagId: string) => string;
    removeProjectTag: (projectTagId: string) => void;
    removeProjectTagByIds: (projectId: string, tagId: string) => void;
    getProjectTags: (projectId: string) => Tag[];
    getTagProjects: (tagId: string) => string[];

    // Person-Tag association operations
    addPersonTag: (personId: string, tagId: string) => string;
    removePersonTagByIds: (personId: string, tagId: string) => void;
    getPersonTags: (personId: string) => Tag[];
    getTagPeople: (tagId: string) => string[];

    // Assignment-Tag association operations
    addAssignmentTag: (
        assignmentId: string,
        tagId: string,
        level?: RequirementLevel
    ) => string;
    setAssignmentTagLevel: (
        assignmentId: string,
        tagId: string,
        level: RequirementLevel
    ) => void;
    removeAssignmentTagByIds: (assignmentId: string, tagId: string) => void;
    getAssignmentTags: (assignmentId: string) => Tag[];
    getAssignmentRequirements: (
        assignmentId: string
    ) => {tag: Tag; level: RequirementLevel}[];
    getTagAssignments: (tagId: string) => string[];

    // Team-Tag association operations
    addTeamTag: (teamId: string, tagId: string) => string;
    removeTeamTagByIds: (teamId: string, tagId: string) => void;
    getTeamTags: (teamId: string) => Tag[];
    getTagTeams: (tagId: string) => string[];

    // Project-Person association operations
    addProjectPerson: (
        projectPerson: Omit<ProjectPerson, 'id' | 'createdAt'>
    ) => string;
    updateProjectPerson: (id: string, updates: Partial<ProjectPerson>) => void;
    removeProjectPerson: (id: string) => void;
    getProjectPeople: (projectId: string) => ProjectPerson[];
    getPersonProjects: (personId: string) => string[];
    setCurrentPersonId: (personId: string | null) => void;

    // System operations
    resetData: () => void;
    // Loads a hand-authored demo tenant tailored per target audience:
    // 'professional-services' (~280-person consultancy, default) or 'insurer'
    // (~2,900-person multinational insurer). WIPES existing data. SysAdmin-only.
    loadDemoData: (profile?: DemoDataProfile) => void;
    // Seeds a synthetic large tenant (default 5,000 people / 300 teams / 40
    // projects) for enterprise-scale testing. Like loadDemoData it WIPES the
    // customer's existing resource-allocation data first. SysAdmin-only.
    loadScaleTestData: () => void;
}

// Demo tenant flavours served by the load-demo-data endpoint.
export type DemoDataProfile = 'professional-services' | 'insurer';

const ResourceAllocationContext = createContext<
    ResourceAllocationContextType | undefined
>(undefined);

// eslint-disable-next-line react-refresh/only-export-components -- shared context hook must live beside its provider
export const useResourceAllocation = () => {
    const context = useContext(ResourceAllocationContext);
    if (!context) {
        throw new Error(
            'useResourceAllocation must be used within ResourceAllocationProvider'
        );
    }
    return context;
};

interface ResourceAllocationProviderProps {
    children: ReactNode;
}

export const ResourceAllocationProvider: React.FC<
    ResourceAllocationProviderProps
> = ({children}) => {
    // Initialize state with empty default
    const [state, setState] = useState<ResourceAllocationState>({
        people: {},
        teams: {},
        roles: {},
        projects: {},
        phases: {},
        assignments: {},
        allocations: {},
        nonWorkingDays: {},
        personalAvailability: {},
        personalAvailabilityWeeks: {},
        workingHoursConfig: {
            effectiveFromWeek: 1,
            baseHoursPerWeek: 40,
            defaultHoursPerDay: 8
        },
        ragThresholdConfig: {
            effectiveFromWeek: 1,
            // Must match the server defaults (RagThresholdConfig entity +
            // /state fallbacks): amber 50 / red 70 — deliberately low, since
            // additional demand sits on top of the day job — baseline band off.
            additionalDemandAmberThreshold: 50,
            additionalDemandRedThreshold: 70,
            baselineAmberThreshold: null
        },
        utilisationConfig: {
            effectiveFromWeek: 1,
            defaultTargetPercent: 80,
            includeAdditionalDemand: false
        },
        workingHoursConfigVersions: [],
        ragThresholdConfigVersions: [],
        utilisationConfigVersions: [],
        tags: {},
        projectTags: {},
        personTags: {},
        assignmentTags: {},
        teamTags: {},
        projectPeople: {},
        allocatedAssignmentIds: [],
        currentPersonId: null
    });

    const [viewMode, setViewMode] = useSessionStorage<ViewMode>(
        'dashboard_viewMode',
        'project'
    );

    // The what-if lens: null = the published picture (the default). Session-
    // scoped so tabs can hold different lenses; applied centrally inside
    // useAllocationLookup so every calc consumer respects it. (null initial —
    // non-primitive useSessionStorage defaults must be module constants.)
    const [whatIfLens, setWhatIfLens] = useSessionStorage<WhatIfLens | null>(
        'whatif_lens',
        null
    );

    // SignalR and auth hooks
    const {service: signalRService} = useSignalR();
    const {customerId} = useAuthStateManager();
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();

    // Bounded-window loading (Phase 2). `allocations` are the one ever-growing table,
    // so the server only returns the weeks in a buffered window around what's being
    // viewed; everything else loads in full. See docs/engineering/architecture/record-lifecycle-and-state-loading.md.
    const BUFFER_BEHIND = 26;
    const BUFFER_AHEAD = 52;
    // The currently-loaded allocation window; reused by background refetches so they
    // don't silently fall back to loading every week.
    const loadedWindowRef = useRef<{fromWeek: number; toWeek: number} | null>(
        null
    );
    const hasLoadedOnceRef = useRef(false);
    const [isStateLoading, setIsStateLoading] = useState(true);
    const [isWindowLoading, setIsWindowLoading] = useState(false);

    // Read via a ref so refetchState keeps a stable identity: the fetch
    // helper's identity changes on every token refresh (~hourly), and having
    // it in refetchState's deps re-fired the initial-load effect — a full
    // /state refetch per token refresh for no reason.
    const authenticatedFetchRef = useRef(authenticatedFetch);
    authenticatedFetchRef.current = authenticatedFetch;

    // Refetch the authoritative state from the API. Used on initial load, on
    // RefetchState SignalR events, and to self-heal stale client state after a
    // failed mutation (e.g. an assignment referencing a ghost team id). When no
    // window is passed it reuses the currently-loaded one.
    const refetchState = useCallback(
        async (window?: {fromWeek: number; toWeek: number}) => {
            const authenticatedFetch = authenticatedFetchRef.current;
            if (!customerId || !authenticatedFetch) {
                return;
            }

            const win = window ?? loadedWindowRef.current ?? undefined;
            if (hasLoadedOnceRef.current) {
                setIsWindowLoading(true);
            } else {
                setIsStateLoading(true);
            }

            try {
                // Phase 3: request the scoped state — only people/teams referenced
                // by in-scope projects + the user's context load; pickers, admin
                // lists and reports fetch the rest from server-backed endpoints.
                // ROLLBACK: drop the scope param and the server returns the full
                // tenant again (see record-lifecycle-and-state-loading.md).
                const params = new URLSearchParams({scope: 'referenced'});
                if (win) {
                    params.append('fromWeek', String(win.fromWeek));
                    params.append('toWeek', String(win.toWeek));
                }
                const url = `${baseUrl}/api/resource-allocation/state?${params.toString()}`;
                const response = await authenticatedFetch(url);

                if (response.ok) {
                    const data = await response.json();
                    // Defensive: tolerate a response without the newer join maps so a
                    // wholesale setState never leaves these slices undefined.
                    setState({
                        ...data,
                        personTags: data.personTags ?? {},
                        assignmentTags: data.assignmentTags ?? {},
                        teamTags: data.teamTags ?? {}
                    });
                    if (win) {
                        loadedWindowRef.current = win;
                    }
                    hasLoadedOnceRef.current = true;
                } else {
                    console.error(
                        '[ResourceAllocationContext] Failed to load state:',
                        response.statusText
                    );
                }
            } catch (error) {
                console.error(
                    '[ResourceAllocationContext] Error loading state:',
                    error
                );
            } finally {
                setIsStateLoading(false);
                setIsWindowLoading(false);
            }
        },
        [customerId]
    );

    // Ensure the buffered window covers [visibleFrom, visibleTo]. No-op when the
    // requested range is already inside the loaded window; otherwise refetch a new
    // buffered window centred on the requested range. Called by the dashboard and
    // reports whenever their visible/queried range changes.
    const ensureWindowLoaded = useCallback(
        (visibleFrom: number, visibleTo: number) => {
            const loaded = loadedWindowRef.current;
            if (
                loaded &&
                visibleFrom >= loaded.fromWeek &&
                visibleTo <= loaded.toWeek
            ) {
                return;
            }
            refetchState({
                fromWeek: visibleFrom - BUFFER_BEHIND,
                toWeek: visibleTo + BUFFER_AHEAD
            });
        },
        [refetchState]
    );

    // Initial data fetch — anchor the first window on the current week so "today" is
    // always covered. The dashboard's ensureWindowLoaded will widen/shift it to match
    // the user's persisted selection on mount.
    useEffect(() => {
        const currentWeek = getWeekInfo(new Date()).globalWeekNumber;
        refetchState({
            fromWeek: currentWeek - BUFFER_BEHIND,
            toWeek: currentWeek + 26 + BUFFER_AHEAD
        });
    }, [refetchState]);

    // Inbound-event coalescing: a burst of remote events (another client's
    // rapid edits, a catch-up after reconnect) is applied as ONE setState —
    // each state change re-runs the whole calc pipeline, so per-event
    // setStates made N remote events cost N full recomputes.
    const pendingRemoteEventsRef = useRef<Array<{type: string; data: unknown}>>(
        []
    );
    const remoteFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );

    // SignalR event listener — only subscribe for authenticated customer users,
    // since all events are scoped to a customer and require an authenticated fetch
    // to react to RefetchState.
    useEffect(() => {
        if (!customerId) {
            return;
        }

        const flushRemoteEvents = () => {
            remoteFlushTimerRef.current = null;
            const events = pendingRemoteEventsRef.current;
            pendingRemoteEventsRef.current = [];
            if (events.length === 0) return;

            setState(prev =>
                events.reduce((acc, {type, data}) => {
                    if (type in signalRHandlers) {
                        const handler =
                            signalRHandlers[
                                type as keyof typeof signalRHandlers
                            ];
                        // The wire payload's shape is trusted to match the
                        // handler registered for its event type.
                        return handler(acc, data as never);
                    }
                    console.warn(
                        '[ResourceAllocationContext] Unhandled event type:',
                        type
                    );
                    return acc;
                }, prev)
            );
        };

        const handleRemoteUpdate = (payload: {
            type: string;
            data: unknown;
        }) => {
            console.log(
                `[ResourceAllocationContext] Received SignalR event: ${payload.type}`
            );

            const {type} = payload;

            if (type !== 'RefetchState') {
                pendingRemoteEventsRef.current.push(payload);
                if (remoteFlushTimerRef.current === null) {
                    remoteFlushTimerRef.current = setTimeout(
                        flushRemoteEvents,
                        50
                    );
                }
            }

            // Handle RefetchState outside of setState. Jitter the reload
            // (0–2s) so a tenant-wide broadcast doesn't stampede /state with
            // simultaneous full refetches from every connected client.
            if (type === 'RefetchState') {
                const jitterMs = Math.random() * 2000;
                console.log(
                    `[ResourceAllocationContext] RefetchState event received, fetching from API in ${Math.round(jitterMs)}ms`
                );
                setTimeout(() => refetchState(), jitterMs);
            }

            // Keep the server-backed caches honest: people/team mutations on
            // other clients must reach the paged searches, resolve results and
            // the report directory, which live in the TanStack cache rather
            // than this context.
            if (
                type === 'RefetchState' ||
                type.startsWith('Person') ||
                type.startsWith('Team')
            ) {
                queryClient.invalidateQueries({
                    queryKey: ['resource-allocation', 'people']
                });
                queryClient.invalidateQueries({
                    queryKey: ['resource-allocation', 'teams']
                });
                queryClient.invalidateQueries({
                    queryKey: ['resource-allocation', 'resolve']
                });
                queryClient.invalidateQueries({
                    queryKey: ['resource-allocation', 'directory']
                });
            }
        };

        console.log(
            '[ResourceAllocationContext] Subscribing to SignalR events'
        );
        signalRService.on('ReceiveEvent', handleRemoteUpdate);

        // After a reconnect the client has missed an unknown set of events
        // (group membership is lost while down) — one full refetch resyncs.
        const handleResync = () => {
            console.log(
                '[ResourceAllocationContext] SignalR reconnected — refetching state'
            );
            refetchState();
        };
        signalRService.onResync(handleResync);

        return () => {
            signalRService.off('ReceiveEvent', handleRemoteUpdate);
            signalRService.offResync(handleResync);
            if (remoteFlushTimerRef.current !== null) {
                clearTimeout(remoteFlushTimerRef.current);
                remoteFlushTimerRef.current = null;
                pendingRemoteEventsRef.current = [];
            }
        };
    }, [signalRService, customerId, refetchState, queryClient]);

    // Actions read state through this ref so their identities survive state
    // changes (assigned every render, so it is always current before children
    // render). This is what lets the provider value below be memoized.
    const stateRef = useRef(state);
    stateRef.current = state;

    const actionDeps = useMemo(
        () => ({
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId: customerId ?? undefined,
            baseUrl,
            refetchState
        }),
        [authenticatedFetch, signalRService, customerId, refetchState]
    );

    const personActions = usePersonActions(actionDeps);
    const teamActions = useTeamActions(actionDeps);
    const roleActions = useRoleActions(actionDeps);
    const projectActions = useProjectActions(actionDeps);
    const allocationActions = useAllocationActions(actionDeps);
    const availabilityActions = useAvailabilityActions(actionDeps);
    const tagActions = useTagActions(actionDeps);
    const configActions = useConfigActions(actionDeps);

    // Low-level operations
    const resetData = useCallback(async () => {
        if (!customerId || !authenticatedFetch) {
            console.error(
                '[resetData] No customerId or authenticatedFetch available'
            );
            return;
        }

        try {
            console.log('[resetData] Calling backend reset endpoint');
            const response = await authenticatedFetch(
                `${baseUrl}/api/resource-allocation/reset`,
                {
                    method: 'POST'
                }
            );

            if (response.ok) {
                console.log(
                    '[resetData] Reset successful, RefetchState event will be broadcast'
                );
                // The backend will broadcast RefetchState event
                // which will be handled by the SignalR event listener
            } else {
                console.error('[resetData] Reset failed:', response.statusText);
                throw new Error(`Reset failed: ${response.statusText}`);
            }
        } catch (error) {
            console.error('[resetData] Error calling reset endpoint:', error);
            throw error;
        }
    }, [customerId, authenticatedFetch]);

    const loadDemoData = useCallback(
        async (profile: DemoDataProfile = 'professional-services') => {
            if (!customerId || !authenticatedFetch) {
            console.error(
                '[loadDemoData] No customerId or authenticatedFetch available'
            );
            return;
        }

        try {
            console.log(
                `[loadDemoData] Calling backend load demo data endpoint (profile: ${profile})`
            );
            const response = await authenticatedFetch(
                `${baseUrl}/api/resource-allocation/load-demo-data`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({profile})
                }
            );

            if (response.ok) {
                console.log(
                    '[loadDemoData] Load demo data successful, RefetchState event will be broadcast'
                );
                // The backend will broadcast RefetchState event
                // which will be handled by the SignalR event listener
            } else {
                console.error(
                    '[loadDemoData] Load demo data failed:',
                    response.statusText
                );
                throw new Error(
                    `Load demo data failed: ${response.statusText}`
                );
            }
        } catch (error) {
            console.error(
                '[loadDemoData] Error calling load demo data endpoint:',
                error
            );
            throw error;
        }
        },
        [customerId, authenticatedFetch]
    );

    const loadScaleTestData = useCallback(async () => {
        if (!customerId || !authenticatedFetch) {
            console.error(
                '[loadScaleTestData] No customerId or authenticatedFetch available'
            );
            return;
        }

        try {
            // Empty body => server defaults (5,000 people / 300 teams / 40
            // projects). Seeding that volume takes a while — the server
            // broadcasts RefetchState when it finishes.
            const response = await authenticatedFetch(
                `${baseUrl}/api/resource-allocation/load-scale-test-data`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({})
                }
            );

            if (!response.ok) {
                console.error(
                    '[loadScaleTestData] Load scale test data failed:',
                    response.statusText
                );
                throw new Error(
                    `Load scale test data failed: ${response.statusText}`
                );
            }
        } catch (error) {
            console.error(
                '[loadScaleTestData] Error calling load scale test data endpoint:',
                error
            );
            throw error;
        }
    }, [customerId, authenticatedFetch]);

    // Memoized so consumers only re-render when state (or another slice they
    // read) actually changes — the action objects are themselves memoized and
    // identity-stable across state changes.
    const value: ResourceAllocationContextType = useMemo(
        () => ({
            state,
            viewMode,
            setViewMode,
            whatIfLens,
            setWhatIfLens,
            isStateLoading,
            isWindowLoading,
            ensureWindowLoaded,
            ...personActions,
            ...teamActions,
            ...roleActions,
            ...projectActions,
            ...allocationActions,
            ...availabilityActions,
            ...tagActions,
            ...configActions,
            resetData,
            loadDemoData,
            loadScaleTestData
        }),
        [
            state,
            viewMode,
            setViewMode,
            whatIfLens,
            setWhatIfLens,
            isStateLoading,
            isWindowLoading,
            ensureWindowLoaded,
            personActions,
            teamActions,
            roleActions,
            projectActions,
            allocationActions,
            availabilityActions,
            tagActions,
            configActions,
            resetData,
            loadDemoData,
            loadScaleTestData
        ]
    );

    return (
        <ResourceAllocationContext.Provider value={value}>
            {children}
        </ResourceAllocationContext.Provider>
    );
};
